// useSound — Web Audio API sparkle chime synthesizer
// No audio files needed; generates a pleasant sparkle sound purely via oscillators.
// Respects prefers-reduced-motion (treats it as a proxy for "reduce sensory stimulation").
// Persists mute preference in localStorage.

import { useState, useCallback, useRef, useEffect } from 'react';

const SOUND_KEY = 'fashionai-sound-muted';

function getInitialMuted() {
  try {
    const stored = localStorage.getItem(SOUND_KEY);
    if (stored !== null) return stored === 'true';
  } catch {}
  // Default: respect prefers-reduced-motion
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {}
  return false;
}

/**
 * Play a sparkle/chime sound using the Web Audio API.
 * Synthesizes a short ascending arpeggio of sine tones.
 */
function playSparkleChime(ctx) {
  if (!ctx) return;

  const now = ctx.currentTime;
  // Notes: a quick upward sparkle arpeggio (pentatonic-ish)
  const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51]; // C5 E5 G5 C6 E6
  const durations = [0.08, 0.08, 0.08, 0.12, 0.25];
  const offsets = [0, 0.07, 0.14, 0.22, 0.32];

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now + offsets[i]);

    const startGain = i === notes.length - 1 ? 0.18 : 0.12;
    gain.gain.setValueAtTime(0, now + offsets[i]);
    gain.gain.linearRampToValueAtTime(startGain, now + offsets[i] + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, now + offsets[i] + durations[i] + 0.15);

    osc.start(now + offsets[i]);
    osc.stop(now + offsets[i] + durations[i] + 0.2);
  });
}

export default function useSound() {
  const [muted, setMutedState] = useState(getInitialMuted);
  const ctxRef = useRef(null);

  // Ensure AudioContext is created (deferred until first user interaction)
  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) {
      try {
        ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        console.warn('Web Audio API not supported');
      }
    }
    // Resume if suspended (Chrome autoplay policy)
    if (ctxRef.current && ctxRef.current.state === 'suspended') {
      ctxRef.current.resume().catch(() => {});
    }
    return ctxRef.current;
  }, []);

  const toggleMute = useCallback(() => {
    setMutedState(m => {
      const next = !m;
      try { localStorage.setItem(SOUND_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  const playChime = useCallback(() => {
    if (muted) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    try {
      playSparkleChime(ctx);
    } catch (e) {
      console.warn('Sound play failed:', e);
    }
  }, [muted, ensureCtx]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
      }
    };
  }, []);

  return { muted, toggleMute, playChime };
}
